import { MenuListItem, IconArrowDropDown, IconArrowDropUp } from 'cdk-radial';
import { useState, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { labelMessages } from '../messages';
import { CoulmnFilterContainer, StyledInput, StyledMenu } from '../styled';
import { OptionType } from '../types';

export const ColumnFilter = ({
  options,
  onSelection
}: {
  options: OptionType[];
  onSelection: (selectedOptions: OptionType[]) => void;
}): React.JSX.Element => {
  const intl = useIntl();
  const targetRef = useRef();
  const dimensionRef = useRef();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>(options);
  const isAllSelected = selectedOptions.length === options.length;
  const isAnySelected = selectedOptions.length > 0;
  const isIndeterminate = !isAllSelected && isAnySelected;

  const handleSelect = (option: OptionType) => {
    const newSelectedOptions = selectedOptions.filter(
      (opt: OptionType) => opt.value !== option.value
    );
    const isAlreadySelected =
      newSelectedOptions.length !== selectedOptions.length;
    if (isAlreadySelected) {
      setSelectedOptions(newSelectedOptions);
    } else {
      const updatedSelectedOptions = [option, ...selectedOptions];
      setSelectedOptions(updatedSelectedOptions);
    }
  };

  useEffect(() => {
    onSelection(selectedOptions);
  }, [selectedOptions, onSelection]);

  const handleAllSelect = (): void => {
    if (!isAnySelected) {
      const optionsExcludingDisabled = options.filter(
        option => !option.disabled
      );
      return setSelectedOptions(optionsExcludingDisabled);
    }

    return setSelectedOptions([]);
  };

  const getIsOptionSelected = (option: OptionType) =>
    selectedOptions.some((opt: OptionType) => opt.value === option.value);

  const getValue = () => {
    if (isIndeterminate) {
      return selectedOptions.length === 1
        ? selectedOptions[0].label
        : `${selectedOptions.length} ${intl.formatMessage(
            labelMessages.inputSelected
          )}`;
    }

    if (isAllSelected) {
      return `${intl.formatMessage(labelMessages.inputAllSelected)}`;
    }
    return '';
  };

  return (
    <>
      <CoulmnFilterContainer>
        <StyledInput
          icon={isOpen ? <IconArrowDropUp /> : <IconArrowDropDown />}
          id="multi-select-dropdown"
          label={intl.formatMessage(labelMessages.inputColumns)}
          data-testid="multi-select-dropdown"
          labelRef={dimensionRef}
          name="multi-select-dropdown"
          placeholder={intl.formatMessage(labelMessages.inputSelect)}
          ref={targetRef}
          value={getValue() || ''}
        />
      </CoulmnFilterContainer>
      <StyledMenu
        dataTestId="menu-container"
        dimensionRef={dimensionRef}
        isAuto={false}
        labelRef={targetRef}
        multi
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
      >
        <MenuListItem
          onClick={handleAllSelect}
          isSelected={isAllSelected || isIndeterminate}
          indeterminate={isIndeterminate}
          showDivider
        >
          <FormattedMessage
            defaultMessage="Select All"
            description="select all for drop down"
          />
        </MenuListItem>
        {options.map(option => (
          <MenuListItem
            key={option.value}
            isSelected={getIsOptionSelected(option)}
            onClick={() => handleSelect(option)}
            isDisabled={option.disabled}
          >
            {option.label}
          </MenuListItem>
        ))}
      </StyledMenu>
    </>
  );
};
